export const BOOKING_FIELD_TYPES = Object.freeze([
  'text', 'long_text', 'date', 'number', 'single_choice', 'multiple_choice', 'confirmation',
]);

export const BOOKING_FIELD_SCOPES = Object.freeze(['booking', 'attendee']);
export const COURSE_AUDIENCES = Object.freeze(['members', 'non_members', 'everyone']);

export function courseMemberTagOptions(tags = []) {
  const expanded = (Array.isArray(tags) ? tags : []).flatMap((tag) => {
    if (typeof tag === 'string') return [{ name: tag, category: 'Uncategorised' }];
    if (!tag || typeof tag !== 'object') return [];
    const names = tag.type === 'ageRule' && Array.isArray(tag.tagNames)
      ? tag.tagNames
      : [tag.name || tag.tagName];
    return names.map((name) => ({ name: String(name || '').trim(), category: tag.category || (tag.type === 'ageRule' ? 'Age Tags' : 'Uncategorised') }));
  }).filter((tag) => tag.name);
  const seen = new Set();
  return expanded.filter((tag) => {
    const key = tag.name.toLocaleLowerCase();
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  }).sort((left, right) => left.category.localeCompare(right.category) || left.name.localeCompare(right.name));
}

export function courseMoneyInputMinor(value) {
  const normalized = String(value ?? '').replace(',', '.');
  if (normalized === '') return 0;
  if (!/^\d+(?:\.\d{0,2})?$/.test(normalized)) return null;
  return Math.max(0, Math.round(Number(normalized) * 100));
}

function localDateTimeInput(date) {
  const pad = (value) => String(value).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

export function courseSessionTimesForStart(session = {}, nextStart = '', defaultDurationMinutes = 180) {
  if (!nextStart) return { start: '', end: '' };
  const nextStartDate = new Date(nextStart);
  if (Number.isNaN(nextStartDate.getTime())) return { start: nextStart, end: '' };
  const previousStart = new Date(session.start || '');
  const previousEnd = new Date(session.end || '');
  const previousDuration = previousEnd.getTime() - previousStart.getTime();
  const duration = Number.isFinite(previousDuration) && previousDuration > 0
    ? previousDuration
    : Math.max(1, Number(defaultDurationMinutes || 180)) * 60 * 1000;
  return {
    start: nextStart,
    end: localDateTimeInput(new Date(nextStartDate.getTime() + duration)),
  };
}

function calendarOffset(value, amount, unit) {
  const date = new Date(value || '');
  if (Number.isNaN(date.getTime())) return '';
  if (unit === 'months') {
    const day = date.getDate();
    date.setDate(1);
    date.setMonth(date.getMonth() + amount);
    const lastDay = new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
    date.setDate(Math.min(day, lastDay));
  } else {
    date.setDate(date.getDate() + amount * (unit === 'weeks' ? 7 : 1));
  }
  return localDateTimeInput(date);
}

export function repeatCourseSession(session = {}, { count = 1, every = 1, unit = 'weeks', makeId } = {}) {
  if (!session.start || !session.end) return [];
  const safeCount = Math.max(1, Math.min(52, Math.floor(Number(count) || 1)));
  const safeEvery = Math.max(1, Math.min(52, Math.floor(Number(every) || 1)));
  const safeUnit = ['days', 'weeks', 'months'].includes(unit) ? unit : 'weeks';
  return Array.from({ length: safeCount }, (_, index) => {
    const amount = safeEvery * (index + 1);
    return {
      ...session,
      id: typeof makeId === 'function' ? makeId(index) : `${session.id || 'session'}_copy_${index + 1}`,
      start: calendarOffset(session.start, amount, safeUnit),
      end: calendarOffset(session.end, amount, safeUnit),
      status: 'scheduled',
    };
  });
}

const TIMING_UNITS = new Set(['hours', 'days', 'weeks']);
const firstAndFinalSession = (sessions = []) => {
  const dated = sessions.filter((session) => session?.start).slice().sort((left, right) => new Date(left.start) - new Date(right.start));
  const ended = sessions.filter((session) => session?.end).slice().sort((left, right) => new Date(left.end) - new Date(right.end));
  return { first: dated[0] || null, final: ended.at(-1) || null };
};
const relativeDateTime = (value, amount, unit) => {
  const date = new Date(value || '');
  if (Number.isNaN(date.getTime())) return '';
  const multiplier = unit === 'weeks' ? 7 * 24 * 60 : unit === 'days' ? 24 * 60 : 60;
  return localDateTimeInput(new Date(date.getTime() - Math.max(1, Number(amount) || 1) * multiplier * 60 * 1000));
};

export function courseTicketTimingConfig(ticket = {}) {
  const legacySchedule = Array.isArray(ticket.priceSchedule) ? ticket.priceSchedule.filter((entry) => entry?.startsAt) : [];
  const legacyEarlyBird = legacySchedule.length === 1 && !ticket.earlyBird;
  const standardPriceMinor = Math.max(0, Number((ticket.standardPriceMinor ?? (legacyEarlyBird ? legacySchedule[0].priceMinor : ticket.priceMinor)) || 0));
  const earlyBirdPriceMinor = Math.max(0, Number((ticket.earlyBird?.priceMinor ?? (legacyEarlyBird ? ticket.priceMinor : standardPriceMinor)) || 0));
  return {
    availability: {
      openMode: ticket.availability?.openMode || (ticket.availableFrom ? 'specific' : 'publication'),
      openOffset: Math.max(1, Number(ticket.availability?.openOffset || 4)),
      openUnit: TIMING_UNITS.has(ticket.availability?.openUnit) ? ticket.availability.openUnit : 'weeks',
      openAt: ticket.availability?.openAt || ticket.availableFrom || '',
      closeMode: ticket.availability?.closeMode || (ticket.availableUntil ? 'specific' : 'first_session_start'),
      closeOffset: Math.max(1, Number(ticket.availability?.closeOffset || 24)),
      closeUnit: TIMING_UNITS.has(ticket.availability?.closeUnit) ? ticket.availability.closeUnit : 'hours',
      closeAt: ticket.availability?.closeAt || ticket.availableUntil || '',
    },
    earlyBird: {
      mode: ticket.earlyBird?.mode || (legacyEarlyBird ? 'specific' : 'none'),
      priceMinor: earlyBirdPriceMinor,
      priceMajor: typeof ticket.earlyBird?.priceMajor === 'string' ? ticket.earlyBird.priceMajor : (earlyBirdPriceMinor / 100).toFixed(2),
      offset: Math.max(1, Number(ticket.earlyBird?.offset || 2)),
      unit: TIMING_UNITS.has(ticket.earlyBird?.unit) ? ticket.earlyBird.unit : 'weeks',
      endsAt: ticket.earlyBird?.endsAt || (legacyEarlyBird ? legacySchedule[0].startsAt : ''),
    },
    standardPriceMinor,
    standardPriceMajor: typeof ticket.standardPriceMajor === 'string' ? ticket.standardPriceMajor : (standardPriceMinor / 100).toFixed(2),
  };
}

const majorTextToMinor = (value, fallback = 0) => {
  const amount = Number(value);
  return Number.isFinite(amount) ? Math.max(0, Math.round(amount * 100)) : Math.max(0, Math.round(Number(fallback) || 0));
};

export function resolveCourseTicketTiming(ticket = {}, sessions = []) {
  const config = courseTicketTimingConfig(ticket);
  const standardPriceMinor = majorTextToMinor(config.standardPriceMajor, config.standardPriceMinor);
  const earlyBirdPriceMinor = majorTextToMinor(config.earlyBird.priceMajor, config.earlyBird.priceMinor);
  const { first, final } = firstAndFinalSession(sessions);
  const availability = config.availability;
  const availableFrom = availability.openMode === 'relative'
    ? relativeDateTime(first?.start, availability.openOffset, availability.openUnit)
    : availability.openMode === 'specific' ? availability.openAt : '';
  const availableUntil = availability.closeMode === 'first_session_start'
    ? first?.start || ''
    : availability.closeMode === 'course_end'
      ? final?.end || ''
      : availability.closeMode === 'relative'
        ? relativeDateTime(first?.start, availability.closeOffset, availability.closeUnit)
        : availability.closeMode === 'specific' ? availability.closeAt : '';
  const earlyBird = config.earlyBird;
  const earlyBirdEnds = earlyBird.mode === 'relative'
    ? relativeDateTime(first?.start, earlyBird.offset, earlyBird.unit)
    : earlyBird.mode === 'specific' ? earlyBird.endsAt : '';
  const earlyBirdEnabled = earlyBird.mode !== 'none' && Boolean(earlyBirdEnds);
  return {
    ...ticket,
    standardPriceMinor,
    availability,
    earlyBird,
    availableFrom,
    availableUntil,
    priceMinor: earlyBirdEnabled ? earlyBirdPriceMinor : standardPriceMinor,
    priceSchedule: earlyBirdEnabled ? [{ startsAt: earlyBirdEnds, priceMinor: standardPriceMinor }] : [],
  };
}

const clean = (value, max = 300) => String(value ?? '').trim().slice(0, max);
const unique = (values = []) => Array.from(new Set(values.filter(Boolean)));

export function normalizeBookingField(field = {}, index = 0) {
  const type = BOOKING_FIELD_TYPES.includes(field.type) ? field.type : 'text';
  const scope = BOOKING_FIELD_SCOPES.includes(field.scope) ? field.scope : 'attendee';
  const key = clean(field.key, 80).replace(/[^a-zA-Z0-9_-]/g, '_') || `field_${index + 1}`;
  const options = ['single_choice', 'multiple_choice'].includes(type)
    ? unique((Array.isArray(field.options) ? field.options : []).map((option) => clean(option, 160)))
    : [];
  return {
    key,
    type,
    scope,
    label: clean(field.label, 180),
    helpText: clean(field.helpText, 1000),
    required: field.required === true,
    sensitive: field.sensitive === true,
    options,
    profileBinding: field.sensitive || type === 'confirmation' ? '' : clean(field.profileBinding, 120),
    declarationVersion: type === 'confirmation' ? clean(field.declarationVersion, 80) || '1' : '',
    declarationWording: type === 'confirmation' ? clean(field.declarationWording || field.label, 4000) : '',
  };
}

export function normalizeBookingForm(form = {}) {
  const seen = new Set();
  const fields = (Array.isArray(form.fields) ? form.fields : []).map(normalizeBookingField).filter((field) => {
    if (!field.label || seen.has(field.key)) return false;
    seen.add(field.key);
    return true;
  });
  return {
    name: clean(form.name, 180) || 'Booking form',
    description: clean(form.description, 1000),
    fields,
  };
}

export function validateBookingForm(form = {}) {
  const normalized = normalizeBookingForm(form);
  const errors = [];
  if (!normalized.name) errors.push('Form name is required.');
  normalized.fields.forEach((field) => {
    if (['single_choice', 'multiple_choice'].includes(field.type) && field.options.length < 1) {
      errors.push(`${field.label} needs at least one choice.`);
    }
    if (field.type === 'confirmation' && !field.declarationWording) {
      errors.push(`${field.label} needs declaration wording.`);
    }
  });
  return { normalized, errors };
}

export function priceForTicket(ticket = {}, now = new Date()) {
  const schedule = (Array.isArray(ticket.priceSchedule) ? ticket.priceSchedule : [])
    .map((entry) => ({
      startsAt: new Date(entry.startsAt),
      priceMinor: Math.max(0, Math.round(Number(entry.priceMinor || 0))),
    }))
    .filter((entry) => !Number.isNaN(entry.startsAt.getTime()))
    .sort((left, right) => left.startsAt - right.startsAt);
  let current = Math.max(0, Math.round(Number(ticket.priceMinor || 0)));
  let next = null;
  schedule.forEach((entry) => {
    if (entry.startsAt <= now) current = entry.priceMinor;
    else if (!next) next = entry;
  });
  return { priceMinor: current, nextPrice: next };
}

export function hydrateCourseBookingNames(bookings = [], namesByAccountId = {}) {
  const lookup = namesByAccountId instanceof Map
    ? (accountId) => namesByAccountId.get(accountId)
    : (accountId) => namesByAccountId?.[accountId];
  const generic = new Set(['course participant', 'participant', 'booking contact', 'member']);
  const usable = (value) => typeof value === 'string' && value.trim() && !generic.has(value.trim().toLowerCase());
  return bookings.map((booking) => {
    const attendees = (booking.attendees || []).map((attendee, index) => {
      const resolved = lookup(attendee.accountId);
      const name = usable(attendee.name)
        ? attendee.name.trim()
        : usable(resolved) ? resolved.trim() : `Participant ${index + 1} (name unavailable)`;
      return { ...attendee, name };
    });
    const resolvedContact = lookup(booking.purchaserAccountId);
    const bookingContactName = usable(booking.bookingContactName)
      ? booking.bookingContactName.trim()
      : usable(resolvedContact) ? resolvedContact.trim() : attendees[0]?.name || 'Booking contact';
    return { ...booking, bookingContactName, attendees };
  });
}

export function paginateCourseOperations(rows = [], requestedPage = 1, pageSize = 10) {
  const size = Math.max(1, Math.floor(Number(pageSize) || 10));
  const pageCount = Math.max(1, Math.ceil(rows.length / size));
  const page = Math.min(pageCount, Math.max(1, Math.floor(Number(requestedPage) || 1)));
  const start = (page - 1) * size;
  return { rows: rows.slice(start, start + size), page, pageCount, start };
}

export function normalizeTicketType(ticket = {}, index = 0) {
  const eligibility = COURSE_AUDIENCES.includes(ticket.eligibility) ? ticket.eligibility : 'everyone';
  return {
    id: clean(ticket.id, 80) || `ticket_${index + 1}`,
    name: clean(ticket.name, 160) || `Ticket ${index + 1}`,
    priceMinor: Math.max(0, Math.round(Number(ticket.priceMinor || 0))),
    capacity: ticket.capacity === '' || ticket.capacity == null ? null : Math.max(1, Math.floor(Number(ticket.capacity || 1))),
    eligibility,
    memberTagNames: eligibility === 'members' ? unique((ticket.memberTagNames || []).map((tag) => clean(tag, 120))) : [],
    availableFrom: clean(ticket.availableFrom, 40),
    availableUntil: clean(ticket.availableUntil, 40),
    priceSchedule: (Array.isArray(ticket.priceSchedule) ? ticket.priceSchedule : []).map((entry) => ({
      startsAt: clean(entry.startsAt, 40),
      priceMinor: Math.max(0, Math.round(Number(entry.priceMinor || 0))),
    })).filter((entry) => entry.startsAt),
  };
}

export function normalizeExtra(extra = {}, index = 0) {
  return {
    id: clean(extra.id, 80) || `extra_${index + 1}`,
    name: clean(extra.name, 160) || `Extra ${index + 1}`,
    scope: extra.scope === 'booking' ? 'booking' : 'attendee',
    priceMinor: Math.max(0, Math.round(Number(extra.priceMinor || 0))),
    capacity: extra.capacity === '' || extra.capacity == null ? null : Math.max(1, Math.floor(Number(extra.capacity || 1))),
    maxQuantity: Math.max(1, Math.floor(Number(extra.maxQuantity || 1))),
  };
}

export function isTicketEligible(ticket = {}, { isMember = false, memberTagNames = [] } = {}) {
  if (ticket.eligibility === 'members' && !isMember) return false;
  if (ticket.eligibility === 'non_members' && isMember) return false;
  if (ticket.eligibility === 'members' && Array.isArray(ticket.memberTagNames) && ticket.memberTagNames.length > 0) {
    const tags = new Set(memberTagNames.map((tag) => typeof tag === 'string' ? tag : tag?.name).filter(Boolean));
    return ticket.memberTagNames.some((tag) => tags.has(tag));
  }
  return true;
}

export function creditableCourseAmount({ baseTicketMinor = 0, refundedBaseMinor = 0, retainedBaseMinor = null, membershipChargeMinor = 0, alreadyUsedMinor = 0 } = {}) {
  const retained = retainedBaseMinor == null
    ? Math.max(0, Number(baseTicketMinor || 0) - Number(refundedBaseMinor || 0))
    : Math.max(0, Number(retainedBaseMinor || 0));
  return Math.max(0, Math.min(
    Math.round(retained),
    Math.max(0, Math.round(Number(membershipChargeMinor || 0))),
    Math.max(0, Math.round(retained - Number(alreadyUsedMinor || 0)))
  ));
}

export function sanitizeOperationalRow(row = {}) {
  const blocked = /(^|_)(uid|userId|accountId|documentPath|storagePath|sourceRef|internalRef)$/i;
  return Object.fromEntries(Object.entries(row).filter(([key]) => !blocked.test(key)));
}

export function courseEditorConfig(course = {}, defaults = {}) {
  const editable = course.editorDraft && typeof course.editorDraft === 'object' ? course.editorDraft : course;
  const bookingFormMode = ['questions', 'none'].includes(editable.bookingFormMode)
    ? editable.bookingFormMode
    : editable.bookingForm?.formId
      ? 'questions'
      : course.status === 'published'
        ? 'none'
        : defaults.bookingFormMode || '';
  const ticketingReviewed = editable.ticketingReviewed === true || (editable.ticketingReviewed == null && course.status === 'published');
  return { ...defaults, ...editable, bookingFormMode, ticketingReviewed, status: editable.status || course.status || 'draft' };
}
